function deriveRestUrl(): string {
  if (import.meta.env.VITE_REST_URL) return import.meta.env.VITE_REST_URL as string;
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host.endsWith('.local.dabblewriter.com') || host === 'localhost') return 'https://rest.local.dabblewriter.com/';
    if (host.endsWith('.test.dabblewriter.com')) return 'https://rest.test.dabblewriter.com/';
  }
  if (import.meta.env.VITE_DEBUG) return 'https://rest.local.dabblewriter.com/';
  return 'https://rest.dabblewriter.com/';
}

const restUrl = deriveRestUrl();

const TEST_STRIPE_PUBLISHABLE_KEY = 'pk_test_MTjhC6rkHFM6egqJz3xp714J';
const LIVE_STRIPE_PUBLISHABLE_KEY = 'pk_live_jz2vNh33pvMngM7J3gO0Ej6U';

/**
 * Publishable key for the same Stripe account as dabble-rest `STRIPE_KEY`.
 *
 * Defaults are host-derived:
 *   - localhost / *.local.dabblewriter.com → test key (local dabble-rest typically uses sk_test_…)
 *   - *.test.dabblewriter.com              → live key (test REST currently wired to live secrets)
 *   - production hostname                  → live key
 *
 * Override with `VITE_STRIPE_PUBLISHABLE_KEY` when local dabble-rest is wired to
 * the live secret and Elements must use `pk_live_…` to match real customer/product IDs.
 *
 * TODO(dabble-3.0): once the test environment fully migrates to a separate Stripe
 * account (later 2026), move `.test.dabblewriter.com` into the test-key branch.
 */
function deriveStripeKey(): string {
  const explicit =
    (typeof import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY === 'string' &&
      import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY.trim()) ||
    (typeof import.meta.env.VITE_STRIPE_KEY === 'string' && import.meta.env.VITE_STRIPE_KEY.trim());
  if (explicit) return explicit;

  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host.endsWith('.local.dabblewriter.com')) {
      return TEST_STRIPE_PUBLISHABLE_KEY;
    }
  }
  return LIVE_STRIPE_PUBLISHABLE_KEY;
}

const DEFAULT_SENTRY_DSN = 'https://f43b76ff950c6601784bdc378ac8d843@o225830.ingest.us.sentry.io/4511418125385728';
const DEFAULT_POSTHOG_TOKEN = 'phc_xR6X3PTdjheARRZ67RzmhYZ2oFBxs29TinsBZsHDimzJ';
const DEFAULT_POSTHOG_HOST = 'https://us.i.posthog.com';

function deriveEnvironment(): 'local' | 'test' | 'production' {
  if (typeof window === 'undefined') return 'production';
  const host = window.location.hostname;
  if (host === 'localhost' || host.endsWith('.local.dabblewriter.com')) return 'local';
  if (host.endsWith('.test.dabblewriter.com')) return 'test';
  return 'production';
}

const ENVIRONMENT = deriveEnvironment();

const explicitSentryDsn = typeof import.meta.env.VITE_SENTRY_DSN === 'string' && import.meta.env.VITE_SENTRY_DSN.trim();
const explicitPosthogKey =
  typeof import.meta.env.VITE_POSTHOG_KEY === 'string' && import.meta.env.VITE_POSTHOG_KEY.trim();
const explicitPosthogHost =
  typeof import.meta.env.VITE_POSTHOG_HOST === 'string' && import.meta.env.VITE_POSTHOG_HOST.trim();

export const REST_URL = restUrl;
export const DOMAIN = 'dabblewriter.com';
export const STRIPE_PUBLISHED_KEY = deriveStripeKey();
export const SENTRY_DSN = explicitSentryDsn || DEFAULT_SENTRY_DSN;
export const SENTRY_ENVIRONMENT = ENVIRONMENT;
// Release identifier (the app version), injected at build time by the deploy
// workflow as VITE_SENTRY_RELEASE. Tagging events with the same value the deploy
// step registers as a Sentry release is what lets "Fixes <ID>" commits auto-resolve
// issues for the shipped release — and reopen only on a genuine regression in a
// later release. Undefined in local/dev builds, so Sentry simply omits the tag.
const explicitSentryRelease =
  typeof import.meta.env.VITE_SENTRY_RELEASE === 'string' && import.meta.env.VITE_SENTRY_RELEASE.trim();
export const SENTRY_RELEASE = explicitSentryRelease || undefined;
export const POSTHOG_TOKEN = explicitPosthogKey || DEFAULT_POSTHOG_TOKEN;
export const POSTHOG_HOST = explicitPosthogHost || DEFAULT_POSTHOG_HOST;
export const POSTHOG_ENVIRONMENT = ENVIRONMENT;
